import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixAchievementsCategoriesV3Category from './achievements-categories-v3-category-categories.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

/** Category is the main entity of CategoriesService that can be used to group programs by */
export interface Category {
  /**
   * Category ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Represents the current state of an item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision
   * @readonly
   */
  revision?: string | null;
  /**
   * Represents the time this Category was created
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Represents the time this Category was last updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /** @maxLength 100 */
  label?: string;
  /**
   * List of programs in that category.
   * @format GUID
   * @maxSize 200
   */
  assignedTo?: string[];
  /** extensible field */
  extendedFields?: ExtendedFields;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CategoriesAssignedToProgram {
  /**
   * Categories which were added to program
   * @format GUID
   * @maxSize 10
   */
  categoriesAdded?: string[];
  /**
   * Categories which were removed from program
   * @format GUID
   * @maxSize 10
   */
  categoriesRemoved?: string[];
}

export interface CategoryAssignedProgramsUpdated {
  /**
   * Programs which were added to category
   * @format GUID
   * @maxSize 10
   */
  programsAdded?: string[];
  /**
   * Programs which were removed from category
   * @format GUID
   * @maxSize 10
   */
  programsRemoved?: string[];
}

export interface CreateCategoryRequest {
  /** Category to be created */
  category: Category;
}

export interface CreateCategoryResponse {
  /** The created Category */
  category?: Category;
}

export interface BulkCreateCategoryRequest {
  /**
   * List of categories you want to create
   * @minSize 1
   * @maxSize 100
   */
  categories: Category[];
  /** Flag to specify if you want to get created entities in response */
  returnEntity?: boolean;
}

export interface BulkCreateCategoryResponse {
  /** Results */
  results?: BulkCategoryResult[];
  /** bulk action metadata */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkCategoryResult {
  /** item metadata */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: Category;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface GetCategoryRequest {
  /**
   * Id of the Category to retrieve
   * @format GUID
   */
  categoryId: string;
}

export interface GetCategoryResponse {
  /** The retrieved Category */
  category?: Category;
}

export interface ListCategoriesRequest {
  /**
   * Ids of categories to list
   * @maxSize 100
   * @format GUID
   */
  categoryIds?: string[];
}

export interface ListCategoriesResponse {
  /**
   * The retrieved categories
   * @maxSize 100
   */
  categories?: Category[];
}

export interface UpdateCategoryRequest {
  /** Category to be updated, may be partial */
  category: Category;
}

export interface UpdateCategoryResponse {
  /** The updated Category */
  category?: Category;
}

export interface DeleteCategoryRequest {
  /**
   * Id of the Category to delete
   * @format GUID
   */
  categoryId: string;
}

export interface DeleteCategoryResponse {}

export interface QueryCategoriesRequest {
  /** WQL expression */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryCategoriesResponse {
  /** The retrieved Categories */
  categories?: Category[];
  /** Metadata for paging */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkAssignCategoriesToProgramRequest {
  /**
   * program ID which categories are changing
   * @format GUID
   */
  programId: string;
  /**
   * Categories which are added to program
   * @format GUID
   * @maxSize 10
   */
  categoriesToAssign?: string[];
  /**
   * Categories which are removed from program
   * @format GUID
   * @maxSize 10
   */
  categoriesToUnassign?: string[];
  /** Flag to specify if you want to get created entities in response */
  returnEntity?: boolean;
}

export interface BulkAssignCategoriesToProgramResponse {
  /** Results */
  results?: BulkAssignCategoryResult[];
  /** bulk action metadata */
  bulkActionMetadata?: BulkActionMetadata;
}

export enum Action {
  UNKNOWN = 'UNKNOWN',
  ASSIGN_PROGRAMS = 'ASSIGN_PROGRAMS',
}

/** @enumType */
export type ActionWithLiterals = Action | 'UNKNOWN' | 'ASSIGN_PROGRAMS';

export interface BulkAssignCategoryResult {
  /** result action */
  action?: ActionWithLiterals;
  /** item metadata */
  itemMetadata?: ItemMetadata;
  /** Only exists if `returnEntity` was set to true in the request */
  item?: Category;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/**
 * Creates a new Category
 * @param category - Category to be created
 * @public
 * @requiredField category
 * @permissionId ONLINE_PROGRAMS.CATEGORY_CREATE
 * @applicableIdentity APP
 * @returns The created Category
 * @fqn wix.achievements.categories.v3.CategoriesService.CreateCategory
 */
export async function createCategory(
  category: Category
): Promise<NonNullablePaths<Category, `label` | `assignedTo`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ category: category });

  const reqOpts =
    ambassadorWixAchievementsCategoriesV3Category.createCategory(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.category!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { category: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['category']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates new categories in a bulk
 * @param categories - List of categories you want to create
 * @public
 * @requiredField categories
 * @permissionId ONLINE_PROGRAMS.CATEGORY_CREATE
 * @applicableIdentity APP
 * @fqn wix.achievements.categories.v3.CategoriesService.BulkCreateCategory
 */
export async function bulkCreateCategory(
  categories: Category[],
  options?: BulkCreateCategoryOptions
): Promise<
  NonNullablePaths<
    BulkCreateCategoryResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.label`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    categories: categories,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixAchievementsCategoriesV3Category.bulkCreateCategory(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          categories: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['categories', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateCategoryOptions {
  /** Flag to specify if you want to get created entities in response */
  returnEntity?: boolean;
}

/**
 * Get a Category by id
 * @param categoryId - Id of the Category to retrieve
 * @public
 * @requiredField categoryId
 * @permissionId ONLINE_PROGRAMS.CATEGORY_READ
 * @applicableIdentity APP
 * @returns The retrieved Category
 * @fqn wix.achievements.categories.v3.CategoriesService.GetCategory
 */
export async function getCategory(
  categoryId: string
): Promise<NonNullablePaths<Category, `label` | `assignedTo`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    categoryId: categoryId,
  });

  const reqOpts =
    ambassadorWixAchievementsCategoriesV3Category.getCategory(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.category!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { categoryId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['categoryId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Get list of categories by list of ids
 * @public
 * @permissionId ONLINE_PROGRAMS.CATEGORY_READ
 * @applicableIdentity APP
 * @fqn wix.achievements.categories.v3.CategoriesService.ListCategories
 */
export async function listCategories(
  options?: ListCategoriesOptions
): Promise<
  NonNullablePaths<
    ListCategoriesResponse,
    `categories` | `categories.${number}.label`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    categoryIds: options?.categoryIds,
  });

  const reqOpts =
    ambassadorWixAchievementsCategoriesV3Category.listCategories(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { categoryIds: '$[0].categoryIds' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListCategoriesOptions {
  /**
   * Ids of categories to list
   * @maxSize 100
   * @format GUID
   */
  categoryIds?: string[];
}

/**
 * Update a Category, supports partial update
 * Pass the latest `revision` for a successful update
 * @param _id - Category ID.
 * @public
 * @requiredField _id
 * @requiredField category
 * @requiredField category.revision
 * @permissionId ONLINE_PROGRAMS.CATEGORY_UPDATE
 * @applicableIdentity APP
 * @returns The updated Category
 * @fqn wix.achievements.categories.v3.CategoriesService.UpdateCategory
 */
export async function updateCategory(
  _id: string,
  category: NonNullablePaths<UpdateCategory, `revision`, 2>
): Promise<NonNullablePaths<Category, `label` | `assignedTo`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    category: { ...category, id: _id },
  });

  const reqOpts =
    ambassadorWixAchievementsCategoriesV3Category.updateCategory(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.category!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { category: '$[1]' },
        explicitPathsToArguments: { 'category.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'category']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateCategory {
  /**
   * Category ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Represents the current state of an item. Each time the item is modified, its `revision` changes by the server. for an update operation to succeed, you MUST pass the latest revision
   * @readonly
   */
  revision?: string | null;
  /**
   * Represents the time this Category was created
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Represents the time this Category was last updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /** @maxLength 100 */
  label?: string;
  /**
   * List of programs in that category.
   * @format GUID
   * @maxSize 200
   */
  assignedTo?: string[];
  /** extensible field */
  extendedFields?: ExtendedFields;
}

/**
 * Delete a Category
 * @param categoryId - Id of the Category to delete
 * @public
 * @requiredField categoryId
 * @permissionId ONLINE_PROGRAMS.CATEGORY_DELETE
 * @applicableIdentity APP
 * @fqn wix.achievements.categories.v3.CategoriesService.DeleteCategory
 */
export async function deleteCategory(categoryId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    categoryId: categoryId,
  });

  const reqOpts =
    ambassadorWixAchievementsCategoriesV3Category.deleteCategory(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { categoryId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['categoryId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Query Categories using [WQL - Wix Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language)
 * @public
 * @permissionId ONLINE_PROGRAMS.CATEGORY_READ
 * @applicableIdentity APP
 * @fqn wix.achievements.categories.v3.CategoriesService.QueryCategories
 */
export function queryCategories(): CategoriesQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    Category,
    'CURSOR',
    QueryCategoriesRequest,
    QueryCategoriesResponse
  >({
    func: async (payload: QueryCategoriesRequest) => {
      const reqOpts =
        ambassadorWixAchievementsCategoriesV3Category.queryCategories(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryCategoriesRequest['query']) => {
      const args = [query, {}] as [QueryCategoriesRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryCategoriesResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.categories,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface CategoriesQueryResult extends QueryCursorResult {
  items: Category[];
  query: CategoriesQueryBuilder;
  next: () => Promise<CategoriesQueryResult>;
  prev: () => Promise<CategoriesQueryResult>;
}

export interface CategoriesQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (propertyName: '_id', value: any) => CategoriesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (propertyName: '_id', value: any) => CategoriesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (propertyName: '_id', value: any) => CategoriesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (propertyName: '_id', value: any) => CategoriesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (propertyName: '_id', value: any) => CategoriesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (propertyName: '_id', value: any) => CategoriesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (propertyName: '_id', value: string) => CategoriesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (propertyName: '_id', value: any[]) => CategoriesQueryBuilder;
  in: (propertyName: '_id', value: any) => CategoriesQueryBuilder;
  exists: (propertyName: '_id', value: boolean) => CategoriesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (...propertyNames: Array<'_id'>) => CategoriesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (...propertyNames: Array<'_id'>) => CategoriesQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => CategoriesQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => CategoriesQueryBuilder;
  find: () => Promise<CategoriesQueryResult>;
}

/**
 * @hidden
 * @fqn wix.achievements.categories.v3.CategoriesService.QueryCategories
 * @requiredField query
 */
export async function typedQueryCategories(
  query: CategoryQuery
): Promise<
  NonNullablePaths<
    QueryCategoriesResponse,
    `categories` | `categories.${number}.label`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixAchievementsCategoriesV3Category.queryCategories(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CategoryQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_id'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  Category,
  CategoryQuerySpec
>;
export type CategoryQuery = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting). 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

/**
 * Assign or unassign categories to a program
 * @param programId - program ID which categories are changing
 * @public
 * @requiredField programId
 * @permissionId ONLINE_PROGRAMS.ASSIGN_CATEGORIES
 * @applicableIdentity APP
 * @fqn wix.achievements.categories.v3.CategoriesService.BulkAssignCategoriesToProgram
 */
export async function bulkAssignCategoriesToProgram(
  programId: string,
  options?: BulkAssignCategoriesToProgramOptions
): Promise<
  NonNullablePaths<
    BulkAssignCategoriesToProgramResponse,
    | `results`
    | `results.${number}.action`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.label`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    programId: programId,
    categoriesToAssign: options?.categoriesToAssign,
    categoriesToUnassign: options?.categoriesToUnassign,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixAchievementsCategoriesV3Category.bulkAssignCategoriesToProgram(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          programId: '$[0]',
          categoriesToAssign: '$[1].categoriesToAssign',
          categoriesToUnassign: '$[1].categoriesToUnassign',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['programId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkAssignCategoriesToProgramOptions {
  /**
   * Categories which are added to program
   * @format GUID
   * @maxSize 10
   */
  categoriesToAssign?: string[];
  /**
   * Categories which are removed from program
   * @format GUID
   * @maxSize 10
   */
  categoriesToUnassign?: string[];
  /** Flag to specify if you want to get created entities in response */
  returnEntity?: boolean;
}
